import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, SIZES, fmt, formatApiError } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import LineItemsSection from "@/components/LineItemsSection";

const blankItem = () => ({ product_id: "", style_id: "", product_name: "", color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), unit_price: 0 });

export default function InvoiceEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [notEditable, setNotEditable] = useState(false);
  const [inv, setInv] = useState(null);
  // Reserved-by-this-invoice quantities, so "available stock" during edit
  // correctly shows what's free INCLUDING what this invoice already holds.
  const [reserved, setReserved] = useState({}); // { "productId|color|size": qty }
  const [amountReceived, setAmountReceived] = useState(0);

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));

  useEffect(() => {
    Promise.all([
      api.get("/products").then(({ data }) => data),
      api.get(`/invoices/${id}`).then(({ data }) => data),
    ]).then(([prods, original]) => {
      setProducts(prods);

      if (original.status !== "DRAFT" && original.status !== "BALANCE_PENDING") {
        setNotEditable(true);
        setLoading(false);
        return;
      }

      setAmountReceived(Number(original.amount_received || 0));

      const res = {};
      (original.items || []).forEach((it) => {
        SIZES.forEach((s) => {
          const q = Number(it.sizes?.[s] || 0);
          if (q > 0) {
            const key = `${it.product_id}|${it.color}|${s}`;
            res[key] = (res[key] || 0) + q;
          }
        });
      });
      setReserved(res);

      setInv({
        invoice_date: original.invoice_date,
        customer_name: original.customer_snapshot?.name || "",
        customer_phone: original.customer_snapshot?.phone || "",
        shipping_address: original.shipping_address || "",
        gst_number: original.gst_number || "",
        shipping_charges: original.shipping_charges || 0,
        discount: original.discount || 0,
        cgst_pct: original.cgst_pct || 0,
        sgst_pct: original.sgst_pct || 0,
        igst_pct: original.igst_pct || 0,
        items: (original.items || []).map((it) => ({
          product_id: it.product_id,
          style_id: it.style_id,
          product_name: it.product_name,
          color: it.color,
          sizes: { ...Object.fromEntries(SIZES.map((s) => [s, 0])), ...it.sizes },
          unit_price: it.unit_price,
        })),
        return_items: (original.return_items || []).map((it) => ({
          product_id: it.product_id,
          style_id: it.style_id,
          product_name: it.product_name,
          color: it.color,
          sizes: { ...Object.fromEntries(SIZES.map((s) => [s, 0])), ...it.sizes },
          unit_price: it.unit_price,
        })),
        return_cgst_pct: original.return_cgst_pct || 0,
        return_sgst_pct: original.return_sgst_pct || 0,
        return_igst_pct: original.return_igst_pct || 0,
      });
      setLoading(false);
    }).catch(() => { setErr("Could not load invoice."); setLoading(false); });
  }, [id]);

  const makeItemHandlers = (field) => ({
    updateItem: (i, patch) => setInv((p) => ({ ...p, [field]: p[field].map((it, ix) => ix === i ? { ...it, ...patch } : it) })),
    updateSize: (i, s, v) => setInv((p) => ({ ...p, [field]: p[field].map((it, ix) => ix === i ? { ...it, sizes: { ...it.sizes, [s]: Math.max(0, Number(v) || 0) } } : it) })),
    addItem: () => setInv((p) => ({ ...p, [field]: [...p[field], blankItem()] })),
    removeItem: (i) => setInv((p) => ({ ...p, [field]: p[field].filter((_, ix) => ix !== i) })),
    onProductChange: (i, productId) => {
      const prod = products.find((x) => x.id === productId);
      setInv((p) => ({
        ...p,
        [field]: p[field].map((it, ix) => ix !== i ? it : (!prod
          ? { ...it, product_id: "", style_id: "", product_name: "", color: "", unit_price: 0 }
          : { ...it, product_id: prod.id, style_id: prod.style_id, product_name: prod.name, color: prod.colors[0]?.color || "", unit_price: prod.selling_price })),
      }));
    },
  });
  const itemHandlers = makeItemHandlers("items");
  const returnHandlers = makeItemHandlers("return_items");

  // Available stock = actual current stock + whatever THIS invoice already has reserved
  // for that exact product/color/size (since it'll be released back before re-deducting).
  const stockFor = (productId, color, size) => {
    const p = products.find((x) => x.id === productId);
    const c = p?.colors.find((c) => c.color === color);
    const actual = c?.sizes[size] ?? 0;
    const key = `${productId}|${color}|${size}`;
    return actual + (reserved[key] || 0);
  };

  const linesFor = (items) => {
    let sub = 0;
    const lines = items.map((it) => {
      const qty = SIZES.reduce((a, s) => a + (it.sizes[s] || 0), 0);
      const lt = qty * (Number(it.unit_price) || 0);
      sub += lt; return { qty, lt };
    });
    return { sub, lines, totalQty: lines.reduce((a, l) => a + l.qty, 0) };
  };

  const totals = useMemo(() => {
    if (!inv) return { sub: 0, grand: 0, lines: [], returnLines: [] };
    const { sub, lines, totalQty } = linesFor(inv.items);
    const taxable = Math.max(0, sub - Number(inv.discount || 0));
    const cgst = taxable * Number(inv.cgst_pct || 0) / 100;
    const sgst = taxable * Number(inv.sgst_pct || 0) / 100;
    const igst = taxable * Number(inv.igst_pct || 0) / 100;
    const saleTotal = taxable + cgst + sgst + igst + Number(inv.shipping_charges || 0);

    const { sub: returnSub, lines: returnLines, totalQty: returnTotalQty } = linesFor(inv.return_items);
    const returnCgst = returnSub * Number(inv.return_cgst_pct || 0) / 100;
    const returnSgst = returnSub * Number(inv.return_sgst_pct || 0) / 100;
    const returnIgst = returnSub * Number(inv.return_igst_pct || 0) / 100;
    const returnTotal = returnSub + returnCgst + returnSgst + returnIgst;

    const grand = saleTotal - returnTotal;
    return {
      sub, cgst, sgst, igst, saleTotal, lines, totalQty,
      returnSub, returnCgst, returnSgst, returnIgst, returnTotal, returnLines, returnTotalQty,
      grand,
    };
  }, [inv]);

  const overstockMap = useMemo(() => {
    const map = {};
    if (!inv) return map;
    inv.items.forEach((it, i) => {
      if (!it.product_id || !it.color) return;
      SIZES.forEach((s) => {
        const v = it.sizes[s] || 0;
        const stock = stockFor(it.product_id, it.color, s);
        if (v > stock) map[`${i}-${s}`] = { qty: v, stock };
      });
    });
    return map;
  }, [inv, products, reserved]);

  const hasOverstock = Object.keys(overstockMap).length > 0;
  const hasReturnItems = !!inv && inv.return_items.some((it) => it.product_id && it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        invoice_date: inv.invoice_date,
        customer_name: inv.customer_name,
        customer_phone: inv.customer_phone,
        shipping_address: inv.shipping_address,
        gst_number: inv.gst_number,
        shipping_charges: Number(inv.shipping_charges) || 0,
        discount: Number(inv.discount) || 0,
        cgst_pct: Number(inv.cgst_pct) || 0,
        sgst_pct: Number(inv.sgst_pct) || 0,
        igst_pct: Number(inv.igst_pct) || 0,
        items: inv.items.map((it) => ({
          product_id: it.product_id, style_id: it.style_id, product_name: it.product_name,
          color: it.color, sizes: it.sizes, unit_price: Number(it.unit_price) || 0,
        })),
        return_items: inv.return_items.filter((it) => it.product_id).map((it) => ({
          product_id: it.product_id, style_id: it.style_id, product_name: it.product_name,
          color: it.color, sizes: it.sizes, unit_price: Number(it.unit_price) || 0,
        })),
        return_cgst_pct: Number(inv.return_cgst_pct) || 0,
        return_sgst_pct: Number(inv.return_sgst_pct) || 0,
        return_igst_pct: Number(inv.return_igst_pct) || 0,
      };
      await api.put(`/invoices/${id}/edit`, payload);
      nav("/invoices");
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const returnItemsValid = !inv || inv.return_items.every((it) => !it.product_id || (it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0)));
  const canSubmit = inv && !hasOverstock && returnItemsValid && totals.grand >= 0 && inv.items.length > 0 && inv.items.every((it) => it.product_id && it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0));

  if (loading) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading invoice...</div>;

  if (notEditable) {
    return (
      <div data-testid="invoice-edit-locked">
        <button className="btn btn-ghost btn-sm" onClick={() => nav("/invoices")} style={{ marginBottom: 14 }}><ArrowLeft size={14}/> Back</button>
        <div className="error-msg">This invoice can no longer be edited — editing is only allowed while an invoice is in Draft or Balance Pending (partially paid) status.</div>
      </div>
    );
  }

  return (
    <div data-testid="invoice-edit-page">
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/invoices")} style={{ marginBottom: 10, paddingLeft: 0 }}><ArrowLeft size={14}/> Cancel Edit</button>
          <h1 className="page-title">Edit Invoice</h1>
          <p className="page-sub">Only available while the invoice is in Draft or Balance Pending (partially paid) status</p>
        </div>
      </div>

      {err && <div className="error-msg" data-testid="invoice-edit-error" style={{ marginBottom: 14 }}>{err}</div>}

      {amountReceived > 0 && (
        <div className="card" data-testid="edit-amount-received-note" style={{ marginBottom: 16, background: "#FFF7ED", borderColor: "#FED7AA" }}>
          ₹ {fmt(amountReceived)} already received on this invoice. Payments are not affected by this edit — only the balance due will be recalculated against the new grand total.
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-form">
          <div className="field"><label>Invoice Date</label><input className="input" type="date" value={inv.invoice_date} onChange={(e) => setInv({ ...inv, invoice_date: e.target.value })}/></div>
          <div className="field"><label>Customer Name</label><input data-testid="edit-cust-name" className="input" value={inv.customer_name} onChange={(e) => setInv({ ...inv, customer_name: e.target.value })}/></div>
          <div className="field"><label>Customer Phone</label><input data-testid="edit-cust-phone" className="input" value={inv.customer_phone} onChange={(e) => setInv({ ...inv, customer_phone: e.target.value })}/></div>
          <div className="field"><label>GST Number</label><input data-testid="edit-gst" className="input" value={inv.gst_number} onChange={(e) => setInv({ ...inv, gst_number: e.target.value })}/></div>
        </div>
        <div className="field" style={{ marginTop: 14 }}><label>Shipping Address</label><textarea data-testid="edit-ship-addr" className="input" rows="2" value={inv.shipping_address} onChange={(e) => setInv({ ...inv, shipping_address: e.target.value })}/></div>
      </div>

      <LineItemsSection
        title="Line Items"
        testIdPrefix="edit-line"
        items={inv.items}
        lines={totals.lines}
        products={products}
        stockFor={stockFor}
        overstockMap={overstockMap}
        onProductChange={itemHandlers.onProductChange}
        onUpdateItem={itemHandlers.updateItem}
        onUpdateSize={itemHandlers.updateSize}
        onAddItem={itemHandlers.addItem}
        onRemoveItem={itemHandlers.removeItem}
        addLabel="Add Item"
        onStockAdjusted={loadProducts}
      />

      <LineItemsSection
        title="Return Items"
        testIdPrefix="edit-return-line"
        items={inv.return_items}
        lines={totals.returnLines}
        products={products}
        stockFor={stockFor}
        onProductChange={returnHandlers.onProductChange}
        onUpdateItem={returnHandlers.updateItem}
        onUpdateSize={returnHandlers.updateSize}
        onAddItem={returnHandlers.addItem}
        onRemoveItem={returnHandlers.removeItem}
        addLabel="Add Return Item"
        variant="return"
        helperText="Items the customer is returning as part of this sale. Their amount is deducted from the invoice total, and stock is added back automatically."
      />

      {hasReturnItems && (
        <div className="card" style={{ marginBottom: 16, border: "1px solid #FCA5A5" }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--danger, #DC2626)", marginBottom: 10 }}>Return Items GST (optional)</h2>
          <div className="grid-3">
            <div className="field"><label>Return CGST %</label><input data-testid="edit-return-cgst" className="input num" type="number" step="0.01" min="0" value={inv.return_cgst_pct} onChange={(e) => setInv({ ...inv, return_cgst_pct: e.target.value })}/></div>
            <div className="field"><label>Return SGST %</label><input data-testid="edit-return-sgst" className="input num" type="number" step="0.01" min="0" value={inv.return_sgst_pct} onChange={(e) => setInv({ ...inv, return_sgst_pct: e.target.value })}/></div>
            <div className="field"><label>Return IGST %</label><input data-testid="edit-return-igst" className="input num" type="number" step="0.01" min="0" value={inv.return_igst_pct} onChange={(e) => setInv({ ...inv, return_igst_pct: e.target.value })}/></div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24, alignItems: "start" }}>
          <div>
            <div className="grid-3" style={{ marginBottom: 12 }}>
              <div className="field"><label>CGST %</label><input className="input num" type="number" step="0.01" min="0" value={inv.cgst_pct} onChange={(e) => setInv({ ...inv, cgst_pct: e.target.value })}/></div>
              <div className="field"><label>SGST %</label><input className="input num" type="number" step="0.01" min="0" value={inv.sgst_pct} onChange={(e) => setInv({ ...inv, sgst_pct: e.target.value })}/></div>
              <div className="field"><label>IGST %</label><input className="input num" type="number" step="0.01" min="0" value={inv.igst_pct} onChange={(e) => setInv({ ...inv, igst_pct: e.target.value })}/></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Shipping Charges (₹)</label><input data-testid="edit-shipping" className="input num" type="number" step="0.01" value={inv.shipping_charges} onChange={(e) => setInv({ ...inv, shipping_charges: e.target.value })}/></div>
              <div className="field"><label>Discount (₹)</label><input className="input num" type="number" step="0.01" value={inv.discount} onChange={(e) => setInv({ ...inv, discount: e.target.value })}/></div>
            </div>
          </div>
          <div style={{ background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 4, padding: 16 }}>
            <Row label="Total Qty" value={<span data-testid="edit-total-qty" className="tabular">{totals.totalQty}</span>}/>
            <Row label="Subtotal" value={`₹ ${fmt(totals.sub)}`}/>
            {totals.cgst > 0 && <Row label={`CGST (${inv.cgst_pct}%)`} value={`+ ₹ ${fmt(totals.cgst)}`}/>}
            {totals.sgst > 0 && <Row label={`SGST (${inv.sgst_pct}%)`} value={`+ ₹ ${fmt(totals.sgst)}`}/>}
            {totals.igst > 0 && <Row label={`IGST (${inv.igst_pct}%)`} value={`+ ₹ ${fmt(totals.igst)}`}/>}
            <Row label="Shipping" value={`+ ₹ ${fmt(inv.shipping_charges)}`}/>
            {hasReturnItems && (
              <div style={{ borderTop: "1px dashed var(--border)", marginTop: 10, paddingTop: 10 }}>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Sale Total</span>} value={`₹ ${fmt(totals.saleTotal)}`}/>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Return Qty</span>} value={<span className="tabular" style={{ color: "var(--danger, #DC2626)" }}>{totals.returnTotalQty}</span>}/>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Return Subtotal</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnSub)}</span>}/>
                {totals.returnCgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return CGST (${inv.return_cgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnCgst)}</span>}/>}
                {totals.returnSgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return SGST (${inv.return_sgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnSgst)}</span>}/>}
                {totals.returnIgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return IGST (${inv.return_igst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnIgst)}</span>}/>}
              </div>
            )}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 10 }}>
              <Row label={<strong>Grand Total</strong>} value={<strong className="tabular" data-testid="edit-grand-total">₹ {fmt(totals.grand)}</strong>} big/>
            </div>
            {amountReceived > 0 && (
              <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 10 }}>
                <Row label="Amount Received" value={`₹ ${fmt(amountReceived)}`}/>
                <Row label={<strong>New Balance Due</strong>} value={<strong className="tabular" data-testid="edit-new-balance">₹ {fmt(Math.max(0, totals.grand - amountReceived))}</strong>} big/>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        {hasOverstock && <span className="error-msg" style={{ flex: 1 }}>Some line quantities exceed available stock. Please correct them.</span>}
        {!hasOverstock && totals.grand < 0 && <span className="error-msg" style={{ flex: 1 }}>Return amount exceeds the invoice items — reduce the return items or add more items.</span>}
        <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
          <button className="btn btn-secondary" onClick={() => nav("/invoices")}>Cancel</button>
          <button data-testid="save-invoice-edit-btn" className="btn btn-primary" disabled={!canSubmit || busy} onClick={submit}>{busy ? "Saving..." : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, big }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: big ? 16 : 14 }}>
      <span style={{ color: big ? "var(--text)" : "var(--text-muted)" }}>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}
